import { Spinner } from "@blueprintjs/core";
import { CollapsibleSection } from "components/common/CollapsibleSection";
import {
    useMapViewRoutingMetadata,
    usePricedConstraintConfig
} from "contexts/RoutingMetadataContext";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { STORAGE_TYPE } from "types/generatorType";
import { getScopeFromScopes, ScopeName } from "types/scope";
import { ScopeViewType } from "types/scopeViewType";
import { CostGraph } from "./CostGraph";
import { CostGraphControls } from "./CostGraphControls";
import "./CostGraphsSection.scss";
import {
    calculateChargingCosts,
    calculateDischargingCosts,
    calculateXBounds,
    calculateYRange,
    downloadCSV,
    downloadImage,
    filterChargingConstraints,
    filterDischargingConstraints,
    formatExportDate,
    formatScopeViewType,
    interpolateCostAtSize,
    type CapacityCostDataPoint,
    type CapacityCostInput,
    type ConstraintType,
    type GraphType
} from "./costGraphsSectionHelpers";
import { CostGraphTooltip } from "./CostGraphTooltip";

const SPINNER_SIZE = 30;

export type CapacityCostsGraphRendererProps = {
    // A bus id for the bus pane, a tap facility id for the line tap pane.
    // Reaches export filenames only.
    readonly exportId: string;
    readonly exportName?: string;
    readonly scope: ScopeName;
    readonly scopeView: ScopeViewType;
    readonly capacityCostsData: ReadonlyArray<CapacityCostInput>;
    readonly constraints: ReadonlyArray<ConstraintType>;
    readonly isLoading: boolean;
    readonly projectEnergySize?: number;
    readonly projectChargingSize?: number;
};

type TooltipProps = {
    readonly active?: boolean;
    readonly payload?: ReadonlyArray<{ payload: CapacityCostDataPoint }>;
    readonly selectedPoint: number | null;
};

type CostGraphConfig = {
    readonly data: CapacityCostDataPoint[];
    readonly showPerMW: boolean;
    readonly onShowPerMWChange: (value: boolean) => void;
    readonly xDomain: [number, number] | null;
    readonly onXDomainChange: (domain: [number, number] | null) => void;
    readonly xBounds: { min: number; max: number };
    readonly yRange: [number, number];
    readonly chartRef: React.RefObject<HTMLDivElement>;
    readonly projectSize?: number;
    readonly costAtProjectSize?: { cost: number; costPerMW: number };
    readonly xAxisLabel: string;
    readonly graphType: GraphType;
};

type CostGraphSectionProps = {
    readonly title: string;
    readonly isOpen: boolean;
    readonly onToggle: () => void;
    readonly isLoading: boolean;
    readonly config: CostGraphConfig;
    readonly costLabel: string;
    readonly exportLabel: string;
    readonly exportName?: string;
    readonly exportId: string;
    readonly showCapacityToggle?: boolean;
    readonly includeCapacity?: boolean;
    readonly onIncludeCapacityChange?: () => void;
    readonly renderTooltip: (tooltipProps: TooltipProps) => React.ReactNode;
};

const CostGraphSection: React.FC<CostGraphSectionProps> = ({
    title,
    isOpen,
    onToggle,
    isLoading,
    config,
    costLabel,
    exportLabel,
    exportName,
    exportId,
    showCapacityToggle,
    includeCapacity,
    onIncludeCapacityChange,
    renderTooltip
}) => {
    const handleDownloadCSV = useCallback(() => {
        downloadCSV(
            config.data,
            config.graphType,
            showCapacityToggle ?? false,
            exportName,
            exportId
        );
    }, [
        config.data,
        config.graphType,
        showCapacityToggle,
        exportName,
        exportId
    ]);

    const handleDownloadImage = useCallback(
        (chartRef: React.RefObject<HTMLDivElement>) => {
            downloadImage(
                chartRef.current,
                exportName,
                exportId,
                config.graphType,
                exportLabel
            );
        },
        [exportName, exportId, config.graphType, exportLabel]
    );

    const renderGraph = (
        chartRef: React.RefObject<HTMLDivElement>,
        includeTitle?: boolean
    ) => (
        <CostGraph
            data={config.data}
            showPerMW={config.showPerMW}
            costLabel={costLabel}
            xAxisLabel={config.xAxisLabel}
            yRange={config.yRange}
            chartRef={chartRef}
            renderTooltip={renderTooltip}
            xDomain={config.xDomain}
            onXDomainChange={config.onXDomainChange}
            projectSize={config.projectSize}
            costAtProjectSize={config.costAtProjectSize?.cost}
            costPerMWAtProjectSize={config.costAtProjectSize?.costPerMW}
            graphTitle={includeTitle ? exportLabel : undefined}
        />
    );

    return (
        <CollapsibleSection title={title} isOpen={isOpen} onToggle={onToggle}>
            {isLoading ? (
                <div className="CostGraphsSection-loading">
                    <Spinner size={SPINNER_SIZE} />
                </div>
            ) : (
                <>
                    <CostGraphControls
                        showPerMW={config.showPerMW}
                        onShowPerMWChange={config.onShowPerMWChange}
                        showCapacityToggle={showCapacityToggle}
                        includeCapacity={includeCapacity}
                        onIncludeCapacityChange={onIncludeCapacityChange}
                        xDomain={config.xDomain}
                        onXDomainChange={config.onXDomainChange}
                        dataBounds={config.xBounds}
                        onDownloadCSV={handleDownloadCSV}
                        onDownloadImage={handleDownloadImage}
                        sidebarChartRef={config.chartRef}
                        title={title}
                        renderExpandedGraph={(chartRef) =>
                            renderGraph(chartRef, true)
                        }
                    />
                    {renderGraph(config.chartRef)}
                </>
            )}
        </CollapsibleSection>
    );
};

export const CapacityCostsGraphRenderer: React.FC<
    CapacityCostsGraphRendererProps
> = (props) => {
    const {
        exportId,
        exportName,
        capacityCostsData,
        constraints,
        projectEnergySize,
        projectChargingSize,
        scope,
        scopeView,
        isLoading
    } = props;

    const { generator, acceptableScopes } = useMapViewRoutingMetadata();
    const {
        hasAllocatedCost,
        showCapacity,
        showChargingSize,
        allowCapacityToggleInCostGraph = true,
        showChargingCostGraph = true
    } = usePricedConstraintConfig();
    const shouldShowCharging = generator === STORAGE_TYPE && showChargingSize;
    const shouldShowChargingGraph = shouldShowCharging && showChargingCostGraph;

    // Generate export label for modal
    const exportLabel = useMemo(() => {
        const scopeData = getScopeFromScopes(acceptableScopes, scope);
        const scopeDisplayName = scopeData?.display_name ?? scope;
        const scopeViewLabel = formatScopeViewType(scopeView);
        const dateStr = formatExportDate();
        return `Nira Export ${dateStr} - ${scopeDisplayName} (${scopeViewLabel})`;
    }, [acceptableScopes, scope, scopeView]);

    // Section collapse states
    const [isOpen, setOpen] = useState(false);
    const [isDischargingOpen, setDischargingOpen] = useState(false);
    const [isChargingOpen, setChargingOpen] = useState(false);

    // Toggle states for each graph
    const [showPerMW, setShowPerMW] = useState(true);
    const [showChargingPerMW, setShowChargingPerMW] = useState(true);
    const [includeCapacity, setIncludeCapacity] = useState(true);
    const [dischargingXDomain, setDischargingXDomain] = useState<
        [number, number] | null
    >(null);
    const [chargingXDomain, setChargingXDomain] = useState<
        [number, number] | null
    >(null);

    const dischargingChartRef = useRef<HTMLDivElement>(null);
    const chargingChartRef = useRef<HTMLDivElement>(null);

    // Use capacity costs data directly - no conversion needed
    const capacityCosts = capacityCostsData;

    // Filter constraints based on type
    const dischargingConstraints = useMemo(
        () =>
            shouldShowCharging
                ? filterDischargingConstraints(constraints)
                : [...constraints],
        [shouldShowCharging, constraints]
    );

    const chargingConstraints = useMemo(
        () =>
            shouldShowChargingGraph
                ? filterChargingConstraints(constraints)
                : [],
        [shouldShowChargingGraph, constraints]
    );

    // Calculate chart data
    const dischargingChartDataFull = useMemo(
        () =>
            calculateDischargingCosts(
                dischargingConstraints,
                hasAllocatedCost,
                capacityCosts,
                false
            ),
        [dischargingConstraints, hasAllocatedCost, capacityCosts]
    );

    const dischargingChartData = useMemo(
        () =>
            includeCapacity
                ? dischargingChartDataFull
                : calculateDischargingCosts(
                      dischargingConstraints,
                      hasAllocatedCost,
                      capacityCosts,
                      true
                  ),
        [
            includeCapacity,
            dischargingChartDataFull,
            dischargingConstraints,
            hasAllocatedCost,
            capacityCosts
        ]
    );

    const chargingChartData = useMemo(
        () =>
            shouldShowChargingGraph
                ? calculateChargingCosts(
                      chargingConstraints,
                      hasAllocatedCost,
                      capacityCosts
                  )
                : [],
        [
            shouldShowChargingGraph,
            chargingConstraints,
            hasAllocatedCost,
            capacityCosts
        ]
    );

    const costLabel = hasAllocatedCost ? "Allocated Cost" : "Total Cost";

    // Pre-sort chart data for interpolation
    const sortedDischargingChartData = useMemo(
        () => [...dischargingChartData].sort((a, b) => a.xValue - b.xValue),
        [dischargingChartData]
    );

    const sortedChargingChartData = useMemo(
        () => [...chargingChartData].sort((a, b) => a.xValue - b.xValue),
        [chargingChartData]
    );

    // Calculate Y-axis ranges
    const dischargingYRange = useMemo(
        () => calculateYRange(dischargingChartData, showPerMW),
        [dischargingChartData, showPerMW]
    );

    const chargingYRange = useMemo(
        () => calculateYRange(chargingChartData, showChargingPerMW),
        [chargingChartData, showChargingPerMW]
    );

    // Calculate X-axis bounds for zoom controls
    const dischargingXBounds = useMemo(
        () => calculateXBounds(dischargingChartData),
        [dischargingChartData]
    );

    const chargingXBounds = useMemo(
        () => calculateXBounds(chargingChartData),
        [chargingChartData]
    );

    // Calculate cost at project size
    const dischargingCostAtProjectSize = useMemo(
        () =>
            projectEnergySize !== undefined
                ? interpolateCostAtSize(
                      sortedDischargingChartData,
                      projectEnergySize
                  )
                : undefined,
        [sortedDischargingChartData, projectEnergySize]
    );

    const chargingCostAtProjectSize = useMemo(
        () =>
            projectChargingSize !== undefined && shouldShowChargingGraph
                ? interpolateCostAtSize(
                      sortedChargingChartData,
                      projectChargingSize
                  )
                : undefined,
        [sortedChargingChartData, projectChargingSize, shouldShowChargingGraph]
    );

    // Toggle handlers
    const handleToggleIncludeCapacity = useCallback(() => {
        setIncludeCapacity((prev) => !prev);
    }, []);

    // Tooltip renderers
    const renderDischargingTooltip = useCallback(
        (tooltipProps: TooltipProps) => (
            <CostGraphTooltip
                {...tooltipProps}
                costLabel={costLabel}
                showCapacity={showCapacity}
                shouldShowCharging={shouldShowCharging}
            />
        ),
        [costLabel, showCapacity, shouldShowCharging]
    );

    const renderChargingTooltip = useCallback(
        (tooltipProps: TooltipProps) => (
            <CostGraphTooltip
                {...tooltipProps}
                costLabel={costLabel}
                showCapacity={false}
                shouldShowCharging={true}
            />
        ),
        [costLabel]
    );

    // Graph configurations
    const dischargingConfig: CostGraphConfig = useMemo(
        () => ({
            data: dischargingChartData,
            showPerMW,
            onShowPerMWChange: setShowPerMW,
            xDomain: dischargingXDomain,
            onXDomainChange: setDischargingXDomain,
            xBounds: dischargingXBounds,
            yRange: dischargingYRange,
            chartRef: dischargingChartRef,
            projectSize: projectEnergySize,
            costAtProjectSize: dischargingCostAtProjectSize,
            xAxisLabel: "Project Energy Size (MW)",
            graphType: "discharging" as GraphType
        }),
        [
            dischargingChartData,
            showPerMW,
            dischargingXDomain,
            dischargingXBounds,
            dischargingYRange,
            projectEnergySize,
            dischargingCostAtProjectSize
        ]
    );

    const chargingConfig: CostGraphConfig = useMemo(
        () => ({
            data: chargingChartData,
            showPerMW: showChargingPerMW,
            onShowPerMWChange: setShowChargingPerMW,
            xDomain: chargingXDomain,
            onXDomainChange: setChargingXDomain,
            xBounds: chargingXBounds,
            yRange: chargingYRange,
            chartRef: chargingChartRef,
            projectSize: projectChargingSize,
            costAtProjectSize: chargingCostAtProjectSize,
            xAxisLabel: "Project Charging Size (MW)",
            graphType: "charging" as GraphType
        }),
        [
            chargingChartData,
            showChargingPerMW,
            chargingXDomain,
            chargingXBounds,
            chargingYRange,
            projectChargingSize,
            chargingCostAtProjectSize
        ]
    );

    // Render single "Cost Graph" section when not showing charging
    if (!shouldShowCharging) {
        return (
            <div>
                <CostGraphSection
                    title="Cost Graph"
                    isOpen={isOpen}
                    onToggle={() => setOpen((prev) => !prev)}
                    isLoading={isLoading}
                    config={dischargingConfig}
                    costLabel={costLabel}
                    exportLabel={exportLabel}
                    exportName={exportName}
                    exportId={exportId}
                    showCapacityToggle={
                        showCapacity && allowCapacityToggleInCostGraph
                    }
                    includeCapacity={includeCapacity}
                    onIncludeCapacityChange={handleToggleIncludeCapacity}
                    renderTooltip={renderDischargingTooltip}
                />
            </div>
        );
    }

    // Render two separate graph sections for storage mode
    return (
        <div>
            <CostGraphSection
                title="Discharging Cost Graph"
                isOpen={isDischargingOpen}
                onToggle={() => setDischargingOpen((prev) => !prev)}
                isLoading={isLoading}
                config={dischargingConfig}
                costLabel={costLabel}
                exportLabel={exportLabel}
                exportName={exportName}
                exportId={exportId}
                showCapacityToggle={
                    showCapacity && allowCapacityToggleInCostGraph
                }
                includeCapacity={includeCapacity}
                onIncludeCapacityChange={handleToggleIncludeCapacity}
                renderTooltip={renderDischargingTooltip}
            />
            {shouldShowChargingGraph && (
                <CostGraphSection
                    title="Charging Cost Graph"
                    isOpen={isChargingOpen}
                    onToggle={() => setChargingOpen((prev) => !prev)}
                    isLoading={isLoading}
                    config={chargingConfig}
                    costLabel={costLabel}
                    exportLabel={exportLabel}
                    exportName={exportName}
                    exportId={exportId}
                    renderTooltip={renderChargingTooltip}
                />
            )}
        </div>
    );
};
